import React, { useState, useEffect } from 'react';
import './UserManager.css';

const UserManager = ({ authenticatedUser }) => {
    const [activeTab, setActiveTab] = useState('users');
    const [users, setUsers] = useState([]);
    const [projects, setProjects] = useState([]);
    const [selectedProject, setSelectedProject] = useState(null);
    const [projectUsers, setProjectUsers] = useState([]);
    const [availableRoles, setAvailableRoles] = useState([]);
    const [availablePermissions, setAvailablePermissions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    
    // Form states
    const [newUserForm, setNewUserForm] = useState({
        email: '',
        username: '',
        full_name: '',
        password: '',
        is_super_admin: false
    });
    const [userAssignmentForm, setUserAssignmentForm] = useState({
        user_email: '',
        role: 'project_user',
        permissions: []
    });
    const [impersonationForm, setImpersonationForm] = useState({
        target_user_email: '',
        project_id: '',
        duration_hours: 1
    });

    useEffect(() => {
        if (authenticatedUser?.is_super_admin) {
            loadAllUsers();
            loadAllProjects();
            loadRolesAndPermissions();
        }
    }, [authenticatedUser]);

    useEffect(() => {
        if (selectedProject) {
            loadProjectUsers(selectedProject.project_id);
        }
    }, [selectedProject]);

    const loadAllUsers = async () => {
        try {
            setLoading(true);
            const response = await fetch('/admin/users');
            const data = await response.json();
            if (data.success) {
                setUsers(data.users);
            } else {
                setError(data.error);
            }
        } catch (err) {
            setError('Failed to load users');
        } finally {
            setLoading(false);
        }
    };

    const loadAllProjects = async () => {
        try {
            setLoading(true);
            const response = await fetch('/admin/projects');
            const data = await response.json();
            if (data.success) {
                setProjects(data.projects);
            } else {
                setError(data.error);
            }
        } catch (err) {
            setError('Failed to load projects');
        } finally {
            setLoading(false);
        }
    };

    const loadProjectUsers = async (projectId) => {
        try {
            setLoading(true);
            const response = await fetch(`/projects/${projectId}/users`);
            const data = await response.json();
            if (data.success) {
                setProjectUsers(data.users);
            } else {
                setError(data.error);
            }
        } catch (err) {
            setError('Failed to load project users');
        } finally {
            setLoading(false);
        }
    };

    const loadRolesAndPermissions = async () => {
        try {
            const [rolesResponse, permissionsResponse] = await Promise.all([
                fetch('/roles'),
                fetch('/permissions')
            ]);
            
            const rolesData = await rolesResponse.json();
            const permissionsData = await permissionsResponse.json();
            
            if (rolesData.success) {
                setAvailableRoles(rolesData.roles);
            }
            if (permissionsData.success) {
                setAvailablePermissions(permissionsData.permissions);
            }
        } catch (err) {
            setError('Failed to load roles and permissions');
        }
    };

    const createUser = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);
            const response = await fetch('/users', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(newUserForm)
            });
            
            const data = await response.json();
            if (data.success) {
                setNewUserForm({
                    email: '',
                    username: '',
                    full_name: '',
                    password: '',
                    is_super_admin: false
                });
                loadAllUsers();
                setError(null);
            } else {
                setError(data.error);
            }
        } catch (err) {
            setError('Failed to create user');
        } finally {
            setLoading(false);
        }
    };

    const assignUserToProject = async (e) => {
        e.preventDefault();
        if (!selectedProject) {
            setError('Please select a project first');
            return;
        }
        
        try {
            setLoading(true);
            const response = await fetch(`/users/${userAssignmentForm.user_email}/projects/${selectedProject.project_id}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    role: userAssignmentForm.role,
                    permissions: userAssignmentForm.permissions
                })
            });
            
            const data = await response.json();
            if (data.success) {
                setUserAssignmentForm({
                    user_email: '',
                    role: 'project_user',
                    permissions: []
                });
                loadProjectUsers(selectedProject.project_id);
                setError(null);
            } else {
                setError(data.error);
            }
        } catch (err) {
            setError('Failed to assign user to project');
        } finally {
            setLoading(false);
        }
    };

    const removeUserFromProject = async (userEmail) => {
        if (!selectedProject) return;
        
        try {
            setLoading(true);
            const response = await fetch(`/users/${userEmail}/projects/${selectedProject.project_id}`, {
                method: 'DELETE'
            });
            
            const data = await response.json();
            if (data.success) {
                loadProjectUsers(selectedProject.project_id);
                setError(null);
            } else {
                setError(data.error);
            }
        } catch (err) {
            setError('Failed to remove user from project');
        } finally {
            setLoading(false);
        }
    };

    const startImpersonation = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);
            const response = await fetch('/impersonation/start', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    impersonator_email: authenticatedUser.email,
                    target_user_email: impersonationForm.target_user_email,
                    project_id: impersonationForm.project_id || null,
                    duration_hours: impersonationForm.duration_hours
                })
            });
            
            const data = await response.json();
            if (data.success) {
                // Store impersonation session token
                localStorage.setItem('impersonation_token', data.session_token);
                localStorage.setItem('impersonation_expires', data.expires_at);
                
                // Reload page to apply impersonation
                window.location.reload();
            } else {
                setError(data.error);
            }
        } catch (err) {
            setError('Failed to start impersonation');
        } finally {
            setLoading(false);
        }
    };

    const endImpersonation = async () => {
        const token = localStorage.getItem('impersonation_token');
        if (!token) return;
        
        try {
            await fetch('/impersonation/end', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ session_token: token })
            });
            
            localStorage.removeItem('impersonation_token');
            localStorage.removeItem('impersonation_expires');
            window.location.reload();
        } catch (err) {
            setError('Failed to end impersonation');
        }
    };

    const handlePermissionChange = (permission, checked) => {
        setUserAssignmentForm(prev => ({
            ...prev,
            permissions: checked 
                ? [...prev.permissions, permission]
                : prev.permissions.filter(p => p !== permission)
        }));
    };

    if (!authenticatedUser?.is_super_admin) {
        return (
            <div className="user-manager">
                <div className="access-denied">
                    <h2>🔒 Access Denied</h2>
                    <p>Only super administrators can access the User Manager.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="user-manager">
            <div className="user-manager-header">
                <h1>👥 User Manager</h1>
                <div className="impersonation-status">
                    {localStorage.getItem('impersonation_token') && (
                        <div className="impersonation-banner">
                            <span>🔀 Impersonating: {localStorage.getItem('impersonation_target')}</span>
                            <button onClick={endImpersonation} className="end-impersonation-btn">
                                End Impersonation
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <div className="user-manager-tabs">
                <button 
                    className={activeTab === 'users' ? 'active' : ''} 
                    onClick={() => setActiveTab('users')}
                >
                    👤 Users
                </button>
                <button 
                    className={activeTab === 'projects' ? 'active' : ''} 
                    onClick={() => setActiveTab('projects')}
                >
                    📁 Projects
                </button>
                <button 
                    className={activeTab === 'assignments' ? 'active' : ''} 
                    onClick={() => setActiveTab('assignments')}
                >
                    🔗 User Assignments
                </button>
                <button 
                    className={activeTab === 'impersonation' ? 'active' : ''} 
                    onClick={() => setActiveTab('impersonation')}
                >
                    🔀 Impersonation
                </button>
            </div>

            {error && (
                <div className="error-message">
                    ❌ {error}
                    <button onClick={() => setError(null)}>×</button>
                </div>
            )}

            {loading && (
                <div className="loading">
                    <div className="spinner"></div>
                    Loading...
                </div>
            )}

            <div className="tab-content">
                {/* Users Tab */}
                {activeTab === 'users' && (
                    <div className="users-tab">
                        <div className="create-user-section">
                            <h3>Create New User</h3>
                            <form onSubmit={createUser} className="create-user-form">
                                <div className="form-group">
                                    <label>Email:</label>
                                    <input
                                        type="email"
                                        value={newUserForm.email}
                                        onChange={(e) => setNewUserForm(prev => ({...prev, email: e.target.value}))}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Username:</label>
                                    <input
                                        type="text"
                                        value={newUserForm.username}
                                        onChange={(e) => setNewUserForm(prev => ({...prev, username: e.target.value}))}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Full Name:</label>
                                    <input
                                        type="text"
                                        value={newUserForm.full_name}
                                        onChange={(e) => setNewUserForm(prev => ({...prev, full_name: e.target.value}))}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Password:</label>
                                    <input
                                        type="password"
                                        value={newUserForm.password}
                                        onChange={(e) => setNewUserForm(prev => ({...prev, password: e.target.value}))}
                                        required
                                    />
                                </div>
                                <div className="form-group checkbox">
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={newUserForm.is_super_admin}
                                            onChange={(e) => setNewUserForm(prev => ({...prev, is_super_admin: e.target.checked}))}
                                        />
                                        Super Admin
                                    </label>
                                </div>
                                <button type="submit" disabled={loading}>
                                    Create User
                                </button>
                            </form>
                        </div>

                        <div className="users-list">
                            <h3>All Users ({users.length})</h3>
                            <div className="users-grid">
                                {users.map(user => (
                                    <div key={user.id} className="user-card">
                                        <div className="user-info">
                                            <h4>{user.full_name}</h4>
                                            <p>@{user.username}</p>
                                            <p>{user.email}</p>
                                            {user.is_super_admin && (
                                                <span className="super-admin-badge">👑 Super Admin</span>
                                            )}
                                        </div>
                                        <div className="user-projects">
                                            <strong>Projects:</strong>
                                            {user.projects.length > 0 ? (
                                                <ul>
                                                    {user.projects.map(project => (
                                                        <li key={project.project_id}>
                                                            {project.project_name} ({project.role})
                                                        </li>
                                                    ))}
                                                </ul>
                                            ) : (
                                                <p>No projects assigned</p>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Projects Tab */}
                {activeTab === 'projects' && (
                    <div className="projects-tab">
                        <h3>All Projects ({projects.length})</h3>
                        <div className="projects-grid">
                            {projects.map(project => (
                                <div key={project.id} className="project-card">
                                    <h4>{project.name}</h4>
                                    <p><strong>ID:</strong> {project.project_id}</p>
                                    <p><strong>URL:</strong> {project.base_url}</p>
                                    <p><strong>Users:</strong> {project.user_count}</p>
                                    <p><strong>Created:</strong> {new Date(project.created_at).toLocaleDateString()}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* User Assignments Tab */}
                {activeTab === 'assignments' && (
                    <div className="assignments-tab">
                        <div className="project-selector">
                            <h3>Select Project</h3>
                            <select 
                                value={selectedProject?.project_id || ''} 
                                onChange={(e) => {
                                    const project = projects.find(p => p.project_id === e.target.value);
                                    setSelectedProject(project);
                                }}
                            >
                                <option value="">Choose a project...</option>
                                {projects.map(project => (
                                    <option key={project.id} value={project.project_id}>
                                        {project.name} ({project.project_id})
                                    </option>
                                ))}
                            </select>
                        </div>

                        {selectedProject && (
                            <>
                                <div className="assign-user-section">
                                    <h3>Assign User to {selectedProject.name}</h3>
                                    <form onSubmit={assignUserToProject} className="assign-user-form">
                                        <div className="form-group">
                                            <label>User Email:</label>
                                            <select
                                                value={userAssignmentForm.user_email}
                                                onChange={(e) => setUserAssignmentForm(prev => ({...prev, user_email: e.target.value}))}
                                                required
                                            >
                                                <option value="">Select user...</option>
                                                {users.map(user => (
                                                    <option key={user.id} value={user.email}>
                                                        {user.full_name} ({user.email})
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label>Role:</label>
                                            <select
                                                value={userAssignmentForm.role}
                                                onChange={(e) => setUserAssignmentForm(prev => ({...prev, role: e.target.value}))}
                                            >
                                                {availableRoles.map(role => (
                                                    <option key={role.value} value={role.value}>
                                                        {role.description}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label>Custom Permissions:</label>
                                            <div className="permissions-grid">
                                                {availablePermissions
                                                    .filter(p => p.category === 'project')
                                                    .map(permission => (
                                                        <label key={permission.value} className="permission-checkbox">
                                                            <input
                                                                type="checkbox"
                                                                checked={userAssignmentForm.permissions.includes(permission.value)}
                                                                onChange={(e) => handlePermissionChange(permission.value, e.target.checked)}
                                                            />
                                                            {permission.description}
                                                        </label>
                                                    ))
                                                }
                                            </div>
                                        </div>
                                        <button type="submit" disabled={loading}>
                                            Assign User
                                        </button>
                                    </form>
                                </div>

                                <div className="project-users">
                                    <h3>Users in {selectedProject.name} ({projectUsers.length})</h3>
                                    <div className="project-users-grid">
                                        {projectUsers.map(user => (
                                            <div key={user.id} className="project-user-card">
                                                <div className="user-info">
                                                    <h4>{user.full_name}</h4>
                                                    <p>{user.email}</p>
                                                    <span className={`role-badge role-${user.role}`}>
                                                        {user.role.replace('_', ' ').toUpperCase()}
                                                    </span>
                                                </div>
                                                <div className="user-permissions">
                                                    <strong>Permissions:</strong>
                                                    <ul>
                                                        {user.permissions.map(permission => (
                                                            <li key={permission}>{permission}</li>
                                                        ))}
                                                    </ul>
                                                </div>
                                                <button 
                                                    onClick={() => removeUserFromProject(user.email)}
                                                    className="remove-user-btn"
                                                    disabled={loading}
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* Impersonation Tab */}
                {activeTab === 'impersonation' && (
                    <div className="impersonation-tab">
                        <div className="impersonation-info">
                            <h3>🔀 User Impersonation</h3>
                            <p>Impersonation allows you to test the application as a different user to verify permissions and functionality.</p>
                        </div>

                        <form onSubmit={startImpersonation} className="impersonation-form">
                            <div className="form-group">
                                <label>Target User Email:</label>
                                <select
                                    value={impersonationForm.target_user_email}
                                    onChange={(e) => setImpersonationForm(prev => ({...prev, target_user_email: e.target.value}))}
                                    required
                                >
                                    <option value="">Select user to impersonate...</option>
                                    {users.map(user => (
                                        <option key={user.id} value={user.email}>
                                            {user.full_name} ({user.email})
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Project Context (Optional):</label>
                                <select
                                    value={impersonationForm.project_id}
                                    onChange={(e) => setImpersonationForm(prev => ({...prev, project_id: e.target.value}))}
                                >
                                    <option value="">No specific project</option>
                                    {projects.map(project => (
                                        <option key={project.id} value={project.project_id}>
                                            {project.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Duration (hours):</label>
                                <input
                                    type="number"
                                    min="1"
                                    max="24"
                                    value={impersonationForm.duration_hours}
                                    onChange={(e) => setImpersonationForm(prev => ({...prev, duration_hours: parseInt(e.target.value)}))}
                                />
                            </div>
                            <button type="submit" disabled={loading}>
                                Start Impersonation
                            </button>
                        </form>

                        <div className="impersonation-warning">
                            <h4>⚠️ Important Notes:</h4>
                            <ul>
                                <li>Only super administrators can impersonate users</li>
                                <li>Impersonation sessions are logged for security</li>
                                <li>You can end impersonation at any time</li>
                                <li>Impersonation is for testing purposes only</li>
                            </ul>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UserManager; 